import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Expense, ExpenseListResponse, ApiResponse } from '@/types'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'

export interface ExpenseBankTransaction {
  id: number
  transaction_date: string
  amount: number
  match_status: 'unmatched' | 'auto_matched' | 'manual_matched' | 'needs_review'
  bank_statement_id: number
}

export const expenseKeys = {
  all: ['expenses'] as const,
  lists: () => [...expenseKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...expenseKeys.lists(), filters] as const,
  details: () => [...expenseKeys.all, 'detail'] as const,
  detail: (id: number) => [...expenseKeys.details(), id] as const,
  reconciliation: (id: number) => [...expenseKeys.all, 'reconciliation', id] as const,
}

export interface ExpenseFilterParams {
  page?: number
  property_id?: number
  category?: string
  status?: string
  from?: string
  to?: string
  q?: string
  // EP-97: server-side sort (allow-listed keys: updated_at/expense_date/amount/category)
  sort?: string
  sort_order?: string
  // T129-011 / D-8: completeness filters backed by BE params
  missing_receipt?: boolean
  has_wht?: boolean
}

export function useExpenses(params: ExpenseFilterParams = {}) {
  return useQuery({
    queryKey: expenseKeys.list(params as Record<string, unknown>),
    queryFn: async () => {
      const queryParams: Record<string, unknown> = { page: params.page ?? 1 }
      if (params.property_id) queryParams.property_id = params.property_id
      if (params.category) queryParams.category = params.category
      if (params.status) queryParams.status = params.status
      if (params.from) queryParams.from = params.from
      if (params.to) queryParams.to = params.to
      if (params.q) queryParams.q = params.q
      if (params.sort) queryParams.sort = params.sort
      if (params.sort_order) queryParams.sort_order = params.sort_order
      if (params.missing_receipt) queryParams.missing_receipt = 'true'
      if (params.has_wht) queryParams.has_wht = 'true'
      const { data } = await api.get<ExpenseListResponse>('/api/expenses', { params: queryParams })
      return data
    },
  })
}

export function useExpense(id: number) {
  return useQuery({
    queryKey: expenseKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<Expense>>(`/api/expenses/${id}`)
      return data.data
    },
    enabled: !!id,
  })
}

export function useExpenseBankTransaction(expenseId: number) {
  return useQuery({
    queryKey: expenseKeys.reconciliation(expenseId),
    queryFn: async () => {
      const { data } = await api.get<{ data: ExpenseBankTransaction | null }>(
        `/api/expenses/${expenseId}/bank_transaction`
      )
      return data.data
    },
    enabled: !!expenseId,
  })
}

export interface CreateExpenseInput {
  expense_date: string
  property_id: number
  category: string
  amount: number
  vat_amount?: number
  total_amount?: number
  vendor_name?: string
  vendor_id?: number | null
  wht_rate?: number | null
  description?: string
  payment_method?: string
  status?: string
  paid_by?: string
  notes?: string
  image_urls?: string[]
  receipt_image_urls?: string[]
  maintenance_request_id?: number
  // T130-009: links the expense to the lease it deducts from / returns the deposit for
  lease_id?: number | null
}

export function useCreateExpense() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateExpenseInput) => {
      const { data } = await api.post<ApiResponse<Expense>>('/api/expenses', { expense: input })
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, expenseKeys.lists())
    },
  })
}

export interface UpdateExpenseInput {
  id: number
  expense_date?: string
  property_id?: number
  category?: string
  amount?: number
  vat_amount?: number
  total_amount?: number
  vendor_name?: string
  vendor_id?: number | null
  wht_rate?: number | null
  description?: string
  payment_method?: string
  status?: string
  paid_by?: string
  notes?: string
  image_urls?: string[]
  receipt_image_urls?: string[]
  maintenance_request_id?: number
}

export function useUpdateExpense() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateExpenseInput) => {
      const { data } = await api.patch<ApiResponse<Expense>>(`/api/expenses/${id}`, { expense: input })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, expenseKeys.lists())
      invalidateDetailQueries(queryClient, expenseKeys.detail(data.id))
    },
  })
}

export function useDeleteExpense() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/expenses/${id}`)
      return id
    },
    onSuccess: () => {
      removeListQueries(queryClient, expenseKeys.lists())
    },
  })
}

export function useReimburseExpense() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.patch<ApiResponse<Expense>>(`/api/expenses/${id}/reimburse`)
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, expenseKeys.lists())
      invalidateDetailQueries(queryClient, expenseKeys.detail(data.id))
    },
  })
}

export function useCompleteExpense() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.patch<ApiResponse<Expense>>(`/api/expenses/${id}/complete`)
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, expenseKeys.lists())
      invalidateDetailQueries(queryClient, expenseKeys.detail(data.id))
    },
  })
}
